// Public commons transparency page (#/commons). Derived entirely client-side from
// signed cost + contribution events — anyone can recompute it. Shows deficit AND
// surplus (both disciplining), one-time costs on a separate series, a disclosed
// pro-rata projection, history to genesis, the accountsBasis, and a "reproduce from
// the firehose" note. Aggregate only — never a per-DID row (master §6.1).
import { button } from "../../design/components.js";
import type { CommonsState } from "../../commons/feed.js";
import { commonsTotals, periodSeries, projectCurrentPeriod, type PeriodPoint } from "../../commons/mirror.js";

export type TransparencyCtx = {
  t: (k: string, p?: Record<string, string | number>) => string;
  state: CommonsState;
  nowMicros: number;
  hostCurrency: string;
  accountsBasis: string;
  onChipIn: () => void;
};

const SVGNS = "http://www.w3.org/2000/svg";

function elx(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

function svgEl(tag: string, attrs: Record<string, string | number>): SVGElement {
  const e = document.createElementNS(SVGNS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

function money(v: number, cur: string): string {
  return `${Math.round(v)} ${cur}`;
}

const W = 320;
const H = 120;
const PAD = 8;

/** A net (surplus/deficit) line chart with a zero baseline so surplus shows above and
 *  deficit below — both equally visible (master §6.1). */
function netChart(series: PeriodPoint[]): SVGElement {
  const svg = svgEl("svg", { viewBox: `0 0 ${W} ${H}`, width: W, height: H, role: "img" });
  const nets = series.map((p) => p.net);
  const max = Math.max(1, ...nets.map((n) => Math.abs(n)));
  const zeroY = H / 2;
  svg.append(svgEl("line", { x1: PAD, y1: zeroY, x2: W - PAD, y2: zeroY, stroke: "currentColor", "stroke-opacity": 0.3 }));
  const n = Math.max(1, series.length);
  const pts = series.map((p, i) => {
    const x = n === 1 ? W / 2 : PAD + (i * (W - 2 * PAD)) / (n - 1);
    const y = zeroY - (p.net / max) * (H / 2 - PAD);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  if (pts.length > 0) {
    svg.append(svgEl("polyline", { points: pts.join(" "), fill: "none", stroke: "currentColor", "stroke-width": 2 }));
  }
  return svg;
}

/** A per-period bar series for one-time costs (separate from the run-rate line). */
function oneTimeChart(series: PeriodPoint[]): SVGElement {
  const svg = svgEl("svg", { viewBox: `0 0 ${W} ${H}`, width: W, height: H, role: "img" });
  const max = Math.max(1, ...series.map((p) => p.oneTime));
  const n = Math.max(1, series.length);
  const bw = (W - 2 * PAD) / n * 0.6;
  series.forEach((p, i) => {
    const h = (p.oneTime / max) * (H - 2 * PAD);
    const x = PAD + (i + 0.2) * ((W - 2 * PAD) / n);
    svg.append(svgEl("rect", { x: x.toFixed(1), y: (H - PAD - h).toFixed(1), width: bw.toFixed(1), height: h.toFixed(1), fill: "currentColor", "fill-opacity": 0.6 }));
  });
  return svg;
}

export function transparencyScreen(ctx: TransparencyCtx): HTMLElement {
  const { t, state, hostCurrency } = ctx;
  const root = elx("section", "screen commons-transparency");
  root.append(elx("h1", "commons-transparency__title bx-headline", t("commons.transparency.title")));
  root.append(elx("p", "bx-body", t("commons.transparency.intro")));

  // Chip-in is reachable here too — the Now-tab button links here once you've given —
  // but it belongs at the END (a content-width call to action below the history), not a
  // page-wide button above the figures. Built once, appended last (and in the empty case).
  const chipCta = (): HTMLElement => {
    const wrap = elx("div", "commons-transparency__chip");
    const chip = button({ label: t("commons.card.chipIn"), variant: "primary", onClick: ctx.onChipIn });
    chip.setAttribute("data-action", "commons-chip-in");
    wrap.append(chip);
    return wrap;
  };

  if (state.costs.length === 0 && state.contribs.length === 0) {
    root.append(elx("p", "bx-body", t("commons.card.empty")));
    root.append(chipCta());
    return root;
  }

  const series = periodSeries(state.costs, state.contribs);

  // Deficit / surplus series.
  root.append(elx("div", "bx-microlabel", t("commons.transparency.series")));
  root.append(netChart(series));

  // One-time series, separate.
  root.append(elx("div", "bx-microlabel", t("commons.transparency.oneTimeSeries")));
  root.append(oneTimeChart(series));

  // Disclosed pro-rata projection.
  const proj = projectCurrentPeriod(state.costs, state.contribs, ctx.nowMicros);
  if (proj) {
    root.append(elx("div", "bx-microlabel", t("commons.transparency.projection")));
    root.append(elx("p", "bx-body",
      `${money(proj.projectedContributed, hostCurrency)} / ${money(proj.recurringCost, hostCurrency)} · ${proj.method} · ${t("commons.transparency.estimate")}`));
  }

  // History to genesis.
  root.append(elx("div", "bx-microlabel", t("commons.transparency.history")));
  const table = document.createElement("table");
  table.className = "commons-transparency__history";
  const head = document.createElement("tr");
  for (const col of ["", "colCost", "colContrib", "colNet", "colOneTime"]) {
    head.append(elx("th", "bx-microlabel", col ? t(`commons.transparency.${col}`) : ""));
  }
  table.append(head);
  for (const p of series) {
    const tr = document.createElement("tr");
    tr.setAttribute("data-period-row", p.period.start);
    tr.append(elx("td", "bx-microlabel", p.period.start.slice(0, 10)));
    tr.append(elx("td", "bx-body", money(p.cost, hostCurrency)));
    tr.append(elx("td", "bx-body", money(p.contributed, hostCurrency)));
    tr.append(elx("td", "bx-body", `${p.net >= 0 ? "+" : ""}${money(p.net, hostCurrency)}`));
    tr.append(elx("td", "bx-body", money(p.oneTime, hostCurrency)));
    table.append(tr);
  }
  root.append(table);

  // Cumulative + basis + operator-stated framing.
  const totals = commonsTotals(state.costs, state.contribs);
  root.append(elx("p", "bx-microlabel",
    `${t("commons.card.operatorStated")} · ${totals.deficit >= 0 ? t("commons.card.deficit") : t("commons.card.surplus")} ${money(Math.abs(totals.deficit), hostCurrency)}`));
  root.append(elx("p", "bx-microlabel", `${t("commons.transparency.basis")}: ${ctx.accountsBasis}`));

  // Reproduce-from-the-firehose note.
  root.append(elx("p", "bx-body", t("commons.transparency.reproduce")));

  // Call to action, below all the figures.
  root.append(chipCta());
  return root;
}
